<template>
    <div>
        <!-- app-sidebar -->
        <div
            id="app-sidebar"
            :class="{'block': sidebarShow}"
            class="app-sidebar surface-section hidden lg:block flex-shrink-0 left-0 top-0 z-1 border-right-1 surface-border select-none"
            style="width: 280px;">

            <!-- flex-column -->
            <div class="h-full bg-gray-100">
                <div class="flex justify-content-end align-items-center">
                    <a @click="toggleSidebar" class="cursor-pointer block lg:hidden text-700 mr-3 mt-1 p-ripple">
                        <i class="pi pi-times"></i>
                    </a>
                </div>
                <div class="app-logos flex flex-wrap align-items-center justify-content-center gap-2 p-1 lg:p-3">
                    <img class="app-logo app-logo--gts" :src="logoGtsSrc" alt="GTSNET"/>
                    <span class="app-logo-sep" aria-hidden="true"></span>
                    <img class="app-logo app-logo--mam" :src="logoMamSrc" alt="MAM Tech"/>
                </div>

                <!-- MENU SKELETON (sem Tree — evita bug tabIndex do PrimeVue) -->
                <div v-if="isLoading" class="main-menu w-full bg-gray-100 p-3 flex flex-column gap-3">
                    <Skeleton v-for="n in 6" :key="n" width="11rem" height="1.25rem" />
                </div>

                <!-- MENU CARREGADO -->
                <Tree
                    v-else-if="sidebar.length"
                    :value="sidebar"
                    v-model:expandedKeys="expandedKeys"
                    :filter="true"
                    scrollHeight="calc(100vh - 12rem)"
                    class="main-menu w-full bg-gray-100">
                    <template #default="{node}">
                        <template v-if="node.para && routeExists(node.para)">
                            <!-- .stop evita o Tree capturar o clique antes da navegação (PrimeVue 4) -->
                            <RouterLink
                                class="main-menu-item"
                                :to="{ name: node.para }"
                                @click.stop="closeOnMobile"
                            >{{ node.label }}</RouterLink>
                        </template>
                        <template v-else-if="node.para">
                            <span class="main-menu-item text-color-secondary">{{ node.label }}</span>
                        </template>
                        <template v-else>
                            {{ node.label }}
                        </template>
                    </template>
                </Tree>
            </div>
            <!-- flex-column -->
        </div>
        <!-- app-sidebar -->

        <!-- mobile sidebar backdrop -->
        <div
            v-if="sidebarShow"
            class="mobile-sidebar-backdrop lg:hidden"
            @click="toggleSidebar">
        </div>
    </div>
</template>

<script>
import Tree from 'primevue/tree';
import Skeleton from 'primevue/skeleton';
import MenuService from '@/service/menu.js';

export default {
    name: 'AppSidebar',
    components: {
        Tree,
        Skeleton
    },
    props: {
        sidebarShow: { type: Boolean, default: false },
        usuario:     { type: Object,  default: () => ({}) }
    },
    emits: ['update:sidebarShow'],
    data() {
        return {
            // Caminhos de /public resolvidos em runtime: `src` literal faria o
            // Vite tentar resolvê-los como módulo e quebrar o build.
            logoGtsSrc: '/logo_gts.png',
            // `logo_sidebar.png` é o `mamtech/logo.png` recortado na caixa de
            // tinta: o original tem ~33% de moldura transparente, que no flex
            // viraria espaço morto e encolheria o logo visível.
            logoMamSrc: '/mamtech/logo_sidebar.png',
            isLoading: false,
            expandedKeys: {},
            sidebar: []
        };
    },
    mounted() {
        this.loadMenu();
    },
    methods: {
        toggleSidebar() {
            this.$emit('update:sidebarShow', !this.sidebarShow);
        },
        closeOnMobile() {
            if (window.innerWidth < 1024 && this.sidebarShow) {
                this.$emit('update:sidebarShow', false);
            }
        },
        routeExists(name) {
            return typeof name === 'string' && this.$router.hasRoute(name);
        },
        loadMenu() {
            this.isLoading = true;
            MenuService.side()
                .then(response => {
                    this.sidebar = this.normalizeMenuTree(response.data);
                    this.expandedKeys = {};
                    this.expandAll(this.sidebar);
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Erro ao carregar menu', detail: msg, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        normalizeMenuTree(nodes) {
            if (!Array.isArray(nodes)) {
                return [];
            }

            return nodes.map((node) => {
                const copy = { ...node };

                if (copy.para === 'cancelamento.list' || copy.para === 'cancelamento.lista') {
                    copy.para = 'cancelamento.fila';
                }

                if (copy.para === 'conta.integridade-auditoria') {
                    copy.label = 'Contas sem vínculo';
                }

                if (Array.isArray(copy.children) && copy.children.length) {
                    copy.children = this.normalizeMenuTree(copy.children);
                } else {
                    delete copy.children;
                }

                return copy;
            });
        },
        expandAll(lista) {
            if (!Array.isArray(lista)) {
                return;
            }
            lista.forEach(item => {
                if (!item?.key) {
                    return;
                }
                this.expandedKeys[item.key] = true;
                if (item.children?.length) {
                    this.expandAll(item.children);
                }
            });
        }
    }
}
</script>

<style scoped>
/* A MAM Tech é EMPILHADA (símbolo / "MAMTECH" / "TECNOLOGIA"), 395x202 na arte
   recortada — 1,96:1 contra os 4,85:1 da GTS. Dimensionada por LEGIBILIDADE do
   wordmark, não por área: só 28,2% da altura do bloco é o "MAMTECH" (57 de
   202px), então a altura da caixa tem que ser ~3,5x a do texto que se quer ler.

   Em 36px:  símbolo 18,0px | "MAMTECH" 10,2px | "TECNOLOGIA" 3,2px
   Em 32px (a altura antiga): "MAMTECH" cairia para 9,0px.

   CUSTO ACEITO: "TECNOLOGIA" fica ilegível em qualquer altura que caiba aqui —
   precisaria de ~57px de caixa só para esse texto passar de 5px, e aí a linha
   estoura. É limitação da arte empilhada. Se a linha de apoio precisar ser
   lida, peça ao fornecedor a versão deitada (símbolo ao lado do texto).

   Largura da linha em 24 + 36:
     GTS 116,4 + gap 8 + barra 12 (4 + 2x4 de margem) + gap 8 + MAM 70,4 = 215px
   Cabe em linha única nos dois piores casos:
     desktop 280 - 2x16 (p-3) = 248px
     mobile  min(86vw,320px) - 2x4 (p-1) = 233px num viewport de 280px
   O contêiner tem `flex-wrap` como rede de segurança, mas em nenhum breakpoint
   atual ele quebra. */
.app-logo {
    width: auto;
    max-width: 100%;
}

.app-logo--gts {
    height: 24px;
}

.app-logo--mam {
    height: 36px;
}

/* Barra divisória diagonal. Elemento próprio, não `border-left` na imagem: os
   `filter` abaixo se aplicam ao elemento inteiro, então a borda seria filtrada
   junto e mudaria de cor entre os temas. Cinza com alfa lê nos dois fundos.

   `transform` não reserva espaço no layout: inclinada 25° a barra ocupa ~17px
   na horizontal mas a caixa só reserva 4px, daí a margem lateral — sem ela a
   ponta invadiria as logos. */
.app-logo-sep {
    width: 4px;
    align-self: stretch;
    margin: 0 4px;
    border-radius: 3px;
    background: rgba(128, 128, 128, 0.55);
    transform: rotate(25deg);
}

/* As duas artes foram feitas para o MESMO fundo (claro) e por isso as duas
   precisam ser corrigidas no tema escuro — a MAM Tech inverteu de polaridade
   quando trocamos a arte da maminfo (branca) pela da MAM Tech (preta), então
   quem precisava de filtro no claro agora precisa no escuro.

   Fundos do sidebar LIDOS no navegador (getComputedStyle do .bg-gray-100, não
   supostos): claro #f5f5f5, escuro #141414 (app.css, `.my-app-dark
   .bg-gray-100`). O comentário anterior assumia #f3f4f6 — errado por pouco.

   Medido, não chutado (contraste vs. fundo do sidebar):

     GTS no escuro   sem filtro: cinza 2,94:1  <- ilegível
                     com filtro: cinza 6,97:1, laranja 4,13:1
     MAM no claro    sem filtro: preto 19,26:1, cinza 2,16:1  <- já correta
     MAM no escuro   sem filtro:  preto  1,14:1  <- invisível
                     invert(1):   branco 18,42:1, cinza 2,52:1 <- cinza reprova
                     + brightness(1,3): branco 18,42:1, cinza 3,73:1

   `invert(1)` sozinho foi medido e descartado: a arte tem dois tons (#000000 no
   símbolo/wordmark, #A7A9AC no "TECNOLOGIA"), e inverter ESCURECE o cinza claro
   para #585653 — 2,52:1, abaixo do mínimo de 3:1 e quase colado no fundo. O
   `brightness(1,3)` depois da inversão só levanta o cinza (o preto já saturou em
   branco), devolvendo os 3,73:1 e mantendo a hierarquia original: wordmark forte,
   linha de apoio recessiva. Fator maior clareia mais (1,6 => 5,36:1) mas achata
   a diferença entre os dois tons — 1,3 é o menor que passa dos 3:1.

   No tema claro a MAM Tech NÃO tem regra: a arte preta já é o caso para o qual
   foi desenhada. Só existe a correção do escuro, abaixo.

   O certo mesmo é arte invertida do fornecedor — aí estes 2 filtros saem. */
.my-app-dark .app-logo--mam {
    filter: invert(1) brightness(1.3);
}

.my-app-dark .app-logo--gts {
    /* `hue-rotate(180deg)` depois do `invert()` devolve o matiz: o laranja
       #F58634 vira #C55604 — mesmo hue (25,5°), só mais escuro. `invert()`
       sozinho jogaria o laranja para azul. */
    filter: invert(1) hue-rotate(180deg);
}

.main-menu :deep(.p-tree-root) * {
    font-size: 0.9rem;
}

.main-menu a {
    text-decoration: none;
    color: var(--p-tree-node-color);
}

.router-link-active {
    font-weight: bold;
}

.main-menu :deep(.p-icon) {
    width: 0.8rem !important;
    height: 0.8rem !important;
}

.main-menu :deep(.p-tree-node-toggle-button) {
    width: 0.5rem;
    height: 0.5rem;
}

.main-menu :deep(.p-tree-node-content) {
    padding-top: 0.9;
}

/* O focus ring do Tree PrimeVue v4 é removido globalmente em app.css.
   O seletor correto é .p-tree-node:focus-visible > .p-tree-node-content
   pois o tabIndex fica no <li class="p-tree-node">, não no content. */
.main-menu :deep(a:focus),
.main-menu :deep(a:focus-visible) {
    outline: none !important;
    box-shadow: none !important;
}

/* Evita borda/”linha” dupla do Tree (scroll/flex) junto à borda do sidebar */
.main-menu :deep(.p-tree),
.main-menu :deep(.p-tree-wrapper),
.main-menu :deep(.p-tree-root) {
    border: none !important;
    box-shadow: none !important;
    background: transparent;
}

/* ── Desktop: sidebar fixo ocupando toda a altura ─────────── */
@media (min-width: 1024px) {
    #app-sidebar.app-sidebar {
        position: sticky;
        top: 0;
        height: 100vh;
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
    }
    #app-sidebar .h-full {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overflow-x: hidden;
    }
}



@media (max-width: 1023px) {
    #app-sidebar.app-sidebar {
        position: fixed;
        width: min(86vw, 320px) !important;
        height: 100dvh !important;
        overflow-y: auto;
        z-index: 1202 !important;
        box-shadow: 0 0 0 9999px transparent;
    }

    .mobile-sidebar-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1201;
        background: rgba(0, 0, 0, 0.55);
    }
}
</style>
